import { gsap } from "gsap";

// Modale contatti condivisa (2 colonne). Sequenza IN: la colonna sinistra si apre
// L->R a tutta altezza -> il suo contenuto va in fade E in parallelo si apre la
// colonna destra (L->R) -> il form (pills + Gravity Forms) va in fade.
// La modale viene spostata in <body> per non restare prigioniera di transform/overflow.
const FOCUSABLE = 'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])';
const SERVICE_PREFIX = "Servizio: ";   // iniezione servizio (accordion-servizi)
const TIPOLOGIA_PREFIX = "Tipologia: "; // fallback se manca il campo GF dedicato

const findMessageField = (modal) =>
  modal.querySelector(
    '[data-service-field], textarea[name="your-message"], textarea[name="messaggio"], textarea[name="message"], textarea'
  );

// Inietta "<prefisso><valore>" in testa al messaggio, sostituendo l'eventuale riga
// con lo stesso prefisso (la modale e' condivisa).
const injectLine = (modal, value, prefix) => {
  const field = findMessageField(modal);
  if (!field) return;
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const cleaned = field.value.replace(new RegExp(`^${escaped}[^\\n]*\\n\\n`), "");
  field.value = value ? `${prefix}${value}\n\n${cleaned}` : cleaned;
};

// Pills tipologia: selezione singola (radio) con possibilita' di deselezionare.
// Il valore va nel campo Gravity Forms con CSS class .js-tipologia (input/select/
// textarea) o name contenente "tipologia"; in mancanza, ripiega nel messaggio.
const setupPills = (modal, form) => {
  const inputs = [...modal.querySelectorAll(".contact-modal__pill-input")];
  if (!inputs.length) return;

  // NB: le pill hanno name="<modal_id>-tipologia", quindi matchano anch'esse
  // [name*="tipologia"]. Le escludo (.contact-modal__pill-input) perche' querySelector
  // ritorna il primo match in ordine di DOM e le pill precedono il form GF.
  const gfTarget = () =>
    (form || modal).querySelector(
      '.js-tipologia input, .js-tipologia select, .js-tipologia textarea, [name*="tipologia" i]:not(.contact-modal__pill-input)'
    );

  const apply = (value) => {
    const target = gfTarget();
    if (target) {
      target.value = value;
      target.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      injectLine(modal, value, TIPOLOGIA_PREFIX);
    }
  };

  let current = null;
  inputs.forEach((input) => {
    input.addEventListener("click", () => {
      if (current === input) {
        input.checked = false;
        current = null;
        apply("");
      } else {
        current = input;
        apply(input.value);
      }
    });
  });
};

const buildController = (modal) => {
  const overlay = modal.querySelector("[data-contact-overlay]");
  const aside = modal.querySelector("[data-contact-aside]");
  const asideContent = modal.querySelector("[data-contact-aside-content]");
  const main = modal.querySelector("[data-contact-main]");
  const form = modal.querySelector("[data-contact-form]");
  const closeBtn = modal.querySelector("[data-contact-close]");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Sposta la modale a livello di <body> solo se NON lo e' gia': re-inserire nel
  // DOM il nodo ricarica l'<iframe> AJAX di Gravity Forms e ne rompe il submit.
  // La modale globale del prefooter e' gia' figlia di <body> (template) -> non si
  // tocca, AJAX intatto. Le istanze dentro i blocchi (antenati con transform/overflow)
  // vengono spostate come prima.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  const HIDDEN = "inset(0 100% 0 0)";
  const SHOWN = "inset(0 0% 0 0)";

  const tl = gsap.timeline({
    paused: true,
    onReverseComplete: () => gsap.set(modal, { visibility: "hidden" }),
  });
  tl.set(modal, { visibility: "visible" })
    .fromTo(overlay, { autoAlpha: 0 }, { autoAlpha: 1, duration: reduce ? 0 : 0.3, ease: "power2.out" })
    // 1) colonna sinistra: apertura L->R a tutta altezza
    .fromTo(aside, { clipPath: HIDDEN }, { clipPath: SHOWN, duration: reduce ? 0 : 0.55, ease: "power3.inOut" })
    // 2) contenuto sinistra in fade + colonna destra che si apre (in parallelo)
    .fromTo(asideContent, { autoAlpha: 0 }, { autoAlpha: 1, duration: reduce ? 0 : 0.45, ease: "power2.out" }, ">")
    .fromTo(main, { clipPath: HIDDEN }, { clipPath: SHOWN, duration: reduce ? 0 : 0.55, ease: "power3.inOut" }, "<")
    // 3) form (pills + Gravity Forms) in fade a colonna destra aperta
    .fromTo(form, { autoAlpha: 0 }, { autoAlpha: 1, duration: reduce ? 0 : 0.45, ease: "power2.out" }, ">");

  let lastFocused = null;

  const open = (opener) => {
    lastFocused = opener;
    if (opener && opener.getAttribute("data-service")) {
      injectLine(modal, opener.getAttribute("data-service"), opener.getAttribute("data-service-prefix") || SERVICE_PREFIX);
    }
    document.body.classList.add("nl-modal-open");
    modal.setAttribute("aria-hidden", "false");
    tl.play();
    (closeBtn || modal.querySelector(FOCUSABLE) || modal).focus();
  };

  const close = () => {
    document.body.classList.remove("nl-modal-open");
    modal.setAttribute("aria-hidden", "true");
    tl.reverse();
    if (lastFocused) lastFocused.focus();
  };

  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const items = [...modal.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (closeBtn) closeBtn.addEventListener("click", close);
  if (overlay) overlay.addEventListener("click", close);
  setupPills(modal, form);

  return { open, close };
};

const contactModal = () => {
  const modals = [...document.querySelectorAll("[data-contact-modal]")];
  if (!modals.length) return;

  const controllers = new Map();
  modals.forEach((modal) => controllers.set(modal.id, buildController(modal)));
  const fallback = controllers.values().next().value;

  // Trigger: bottoni [data-contact-open] (con aria-controls) + link href="#open_contacts".
  const openers = [
    ...document.querySelectorAll("[data-contact-open]"),
    ...document.querySelectorAll('a[href="#open_contacts"], a[href$="#open_contacts"]'),
  ];
  openers.forEach((opener) => {
    const isHash = opener.tagName === "A" && (opener.getAttribute("href") || "").endsWith("#open_contacts");
    const id = isHash ? "prefooter-modal" : opener.getAttribute("aria-controls");
    const controller = (id && controllers.get(id)) || fallback;
    if (!controller) return;
    opener.addEventListener("click", (event) => {
      if (isHash) event.preventDefault();
      controller.open(opener);
    });
  });
};

export default contactModal;
